import React from 'react';
import ChildFields from './ExplorerChildFields';
import { useForm, useFieldArray } from 'react-hook-form';
import Navbar from './Navbar';
import { countries } from '../data/countries';
import { authService, childrenService, dashboardService } from '../services/api';
import { useNavigate } from 'react-router-dom';

// Tipos para los datos del formulario
interface ChildData {
  name: string;
  birthdate: string;
}

interface FormData {
  representativeName: string;
  country: string;
  phone: string;
  email: string;
  password: string;
  birthdate?: string; // Opcional por ahora
  numChildren: number;
  children: ChildData[];
  acceptTerms: boolean;
}

/**
 * Componente principal del formulario de registro Princonser Explorer
 */
const ExplorerForm: React.FC = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, watch, control, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      children: [{ name: '', birthdate: '' }],
      numChildren: 1,
      acceptTerms: false,
    },
    mode: 'onChange', // Validar en tiempo real
  });

  // Sincroniza el número de niños con los campos dinámicos
  const numChildren = watch('numChildren');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'children',
  });

  React.useEffect(() => {
    const diff = numChildren - fields.length;
    if (diff > 0) {
      for (let i = 0; i < diff; i++) append({ name: '', birthdate: '' });
    } else if (diff < 0) {
      for (let i = 0; i < -diff; i++) remove(fields.length - 1 - i);
    }
    // eslint-disable-next-line
  }, [numChildren]);

  // Maneja el envío del formulario
  const onSubmit = async (data: FormData) => {
    try {
      // Registrar representante con todos los datos necesarios
      const rep = await authService.register({
        full_name: data.representativeName,
        email: data.email,
        password: data.password,
        country: data.country,
        phone: data.phone,
        birth_date: data.birthdate || '',
      });
      
      console.log('✅ Representante registrado:', rep);
      
      // Iniciar sesión automáticamente
      console.log('🔐 Iniciando login automático...');
      let loginResp;
      try {
        loginResp = await authService.login({
          username: data.email,
          password: data.password,
        });
        console.log('✅ Login exitoso:', loginResp);
      } catch (loginError: any) {
        console.error('❌ Error en login automático:', loginError);
        throw new Error(`Error en login automático: ${loginError.message || 'Error desconocido'}`);
      }
      console.log('🔍 Estructura de loginResp:', {
        hasAccessToken: !!loginResp.access_token,
        hasRefreshToken: !!loginResp.refresh_token,
        hasUser: !!loginResp.user,
        keys: Object.keys(loginResp)
      });
      
      // Verificar que los tokens se hayan guardado correctamente
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      console.log('🔍 Tokens en localStorage después del login:', {
        token: token ? 'SÍ' : 'NO',
        refreshToken: refreshToken ? 'SÍ' : 'NO',
        tokenLength: token?.length || 0,
        refreshTokenLength: refreshToken?.length || 0
      });
      
      if (!token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }
      
      console.log('✅ Token guardado correctamente:', token ? 'SÍ' : 'NO');
      console.log('✅ Refresh token guardado:', refreshToken ? 'SÍ' : 'NO');
      
      // Esperar un poco para asegurar que los tokens estén disponibles
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Probar el dashboard directamente antes de registrar niños
      console.log('🔍 Probando acceso al dashboard...');
      try {
        const dashboardResp = await dashboardService.getDashboard();
        console.log('✅ Dashboard accesible:', dashboardResp);
      } catch (dashboardError) {
        console.error('❌ Error accediendo al dashboard:', dashboardError);
        // Continuar de todas formas
      }
      
      // Registrar niños
      for (const child of data.children) {
        const childResp = await childrenService.createChild({
          full_name: child.name,
          birth_date: child.birthdate,
          country: data.country,
        });
        console.log('✅ Niño registrado:', childResp);
      }
      
      alert('¡Registro enviado correctamente!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('❌ Error en el registro completo:', error);
      let errorMessage = 'Error al registrar. Intenta de nuevo.';
      
      // Manejar errores de Axios
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 422) {
          // Error de validación
          if (data.detail && Array.isArray(data.detail)) {
            const validationErrors = data.detail.map((err: any) => err.msg).join(', ');
            errorMessage = `Error de validación: ${validationErrors}`;
          } else {
            errorMessage = 'Error de validación en los datos enviados.';
          }
        } else if (status === 400) {
          errorMessage = 'Datos inválidos. Por favor verifica la información.';
        } else if (status === 409) {
          errorMessage = 'El email ya está registrado. Por favor usa otro email.';
        } else {
          errorMessage = `Error del servidor (${status}): ${data.message || 'Error desconocido'}`;
        }
      } else if (error instanceof Error) {
        if (error.message.includes('email ya está registrado')) {
          errorMessage = 'El email ya está registrado. Por favor usa otro email.';
        } else if (error.message.includes('Error en código de referido')) {
          errorMessage = 'El código de referido no es válido. Por favor verifica el código.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      alert(errorMessage);
    }
  };

  return (
    <>
      <Navbar currentTheme={1} onThemeChange={() => {}} />
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-yellow-200 via-yellow-100 to-blue-200">
        <section
          className="relative w-full max-w-5xl min-h-[1100px] flex items-center justify-center bg-cover bg-center rounded-3xl shadow-2xl p-2 sm:p-6 md:p-10"
          style={{ backgroundImage: "url('/images/explorer-bg-vert.png')" }}
        >
          <form
            className="w-full max-w-[600px] bg-yellow-100 bg-opacity-90 rounded-3xl shadow-lg p-4 sm:p-6 md:p-8 mt-52 md:mt-72 lg:mt-96 xl:mt-[28rem] mr-20 md:mr-40 lg:mr-60 xl:mr-72 mb-10"
            onSubmit={handleSubmit(onSubmit)}
          >
            <h1 className="text-2xl md:text-3xl font-bold text-center mb-2">Formulario de Registro</h1>
            <h2 className="text-xl md:text-2xl font-bold text-center mb-6 text-yellow-700">PRINCONSER Explorer</h2>
            <div className="mb-4">
              <div className="font-semibold text-lg mb-2 text-center">Datos del Representante</div>
              <input
                className="w-full mb-2 p-2 rounded border border-gray-300 focus:outline-none"
                placeholder="Nombre Completo"
                {...register('representativeName', { 
                  required: 'El nombre completo es requerido',
                  minLength: { value: 2, message: 'El nombre debe tener al menos 2 caracteres' }
                })}
              />
              {errors.representativeName && <span className="text-red-500 text-sm">{errors.representativeName.message}</span>}
              <div className="flex gap-2 mb-2">
                <select
                  className="flex-1 p-2 rounded border border-gray-300 focus:outline-none"
                  {...register('country', { 
                    required: 'El país es requerido',
                    validate: value => value !== '' || 'Por favor selecciona un país'
                  })}
                  defaultValue=""
                >
                  <option value="" disabled>País de Residencia</option>
                  {countries.map((country) => (
                    <option key={country.code + '-' + country.name} value={country.name}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
                <input
                  className="flex-1 p-2 rounded border border-gray-300 focus:outline-none"
                  placeholder="Teléfono (solo números, sin código de país)"
                  onInput={(e) => {
                    // Limpiar automáticamente caracteres no válidos
                    const target = e.target as HTMLInputElement;
                    target.value = target.value.replace(/[^\d]/g, '');
                  }}
                  {...register('phone', { 
                    required: 'El teléfono es requerido',
                    pattern: {
                      value: /^\d{7,15}$/,
                      message: 'El teléfono debe contener solo números (7-15 dígitos)'
                    },
                    validate: {
                      noSpaces: (value) => !value.includes(' ') || 'No se permiten espacios',
                      noSpecialChars: (value) => /^\d+$/.test(value) || 'Solo se permiten números',
                      noCountryCode: (value) => !value.startsWith('+') && !value.startsWith('00') || 'No incluir código de país (+ o 00)'
                    }
                  })}
                />
              </div>
              {/* Campo de fecha de nacimiento oculto temporalmente
              <input
                className="w-full mb-2 p-2 rounded border border-gray-300 focus:outline-none"
                placeholder="Fecha de Nacimiento"
                type="date"
                {...register('birthdate', { required: true })}
              />
              {errors.birthdate && <span className="text-red-500 text-sm">Este campo es requerido</span>}
              */}
              <div className="flex gap-2 mb-2">
                <input
                  className="flex-1 p-2 rounded border border-gray-300 focus:outline-none"
                  placeholder="Correo"
                  type="email"
                  {...register('email', { 
                    required: 'El correo es requerido',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Formato de correo inválido'
                    }
                  })}
                />
                <select
                  className="flex-1 p-2 rounded border border-gray-300 focus:outline-none"
                  {...register('numChildren', { 
                    required: 'El número de niños es requerido',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Debe tener al menos 1 niño' },
                    max: { value: 5, message: 'Máximo 5 niños' }
                  })}
                  defaultValue={1}
                >
                  <option value="" disabled>Número de niños</option>
                  {[1,2,3,4,5].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              {errors.email && <span className="text-red-500 text-sm">{errors.email.message}</span>}
              {errors.numChildren && <span className="text-red-500 text-sm">{errors.numChildren.message}</span>}
              <input
                className="w-full mb-2 p-2 rounded border border-gray-300 focus:outline-none"
                placeholder="Contraseña"
                type="password"
                {...register('password', { 
                  required: 'La contraseña es requerida',
                  minLength: { value: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                })}
              />
              {errors.password && <span className="text-red-500 text-sm">{errors.password.message}</span>}
            </div>
            {/* Campos dinámicos para los niños */}
            {fields.map((field, idx) => (
              <ChildFields
                key={field.id}
                index={idx}
                register={register}
                errors={errors}
              />
            ))}
            
            {/* Checkbox de términos y condiciones */}
            <div className="mt-4 mb-4">
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  {...register('acceptTerms', { required: true })}
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  Acepto los{' '}
                  <a
                    href="/terminos-condiciones"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-600 hover:text-yellow-700 underline font-semibold"
                  >
                    términos y condiciones
                  </a>
                </label>
              </div>
              {errors.acceptTerms && (
                <span className="text-red-500 text-sm block mt-1">
                  Debes aceptar los términos y condiciones para continuar
                </span>
              )}
            </div>
            
            <button
              type="submit"
              className="w-full mt-4 bg-yellow-400 hover:bg-yellow-500 text-white font-bold py-2 px-4 rounded-full transition"
            >
              Registrarse
            </button>
          </form>
        </section>
      </div> {/* Cierre del contenedor principal del formulario */}

      {/* Banners apilados, solo imagen, sin texto, debajo del formulario */}
      <section className="w-full">
        <img src="/images/banners/banner01.png" alt="Banner 1" className="w-full h-auto object-cover" loading="lazy" />
      </section>
      <section className="w-full">
        <img src="/images/banners/banner02.png" alt="Banner 2" className="w-full h-auto object-cover" loading="lazy" />
      </section>
      <section className="w-full">
        <img src="/images/banners/banner03.png" alt="Banner 3" className="w-full h-auto object-cover" loading="lazy" />
      </section>
      <section className="w-full">
        <img src="/images/banners/banner04.png" alt="Banner 4" className="w-full h-auto object-cover" loading="lazy" />
      </section>
    </>
  );
};

export default ExplorerForm; 